<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import HeaderMenu from '@/Components/Public/HeaderMenu.vue'

const { t } = useI18n()

// Theme (light/dark). Public-site-only — stored in localStorage.gp_theme
// and applied via data-mode on documentElement. The boot script in
// application.html.erb reads gp_theme before paint to prevent flash.
const themeMode = ref('light')

function applyTheme(mode) {
  themeMode.value = mode
  if (typeof document === 'undefined') return
  document.documentElement.dataset.mode = mode
  document.documentElement.style.colorScheme = mode
  try { localStorage.setItem('gp_theme', mode) } catch (e) {}
}

function setTheme(mode) {
  if (mode === themeMode.value) return
  applyTheme(mode)
}

onMounted(() => {
  try {
    const stored = localStorage.getItem('gp_theme')
    if (stored === 'dark' || stored === 'light') {
      themeMode.value = stored
    } else if (document.documentElement.dataset.mode === 'dark') {
      themeMode.value = 'dark'
    }
  } catch (e) {}
})
</script>

<template>
  <HeaderMenu :aria-label="t('public.menu.theme')">
    <template #icon>
      <svg v-if="themeMode === 'dark'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"/>
      </svg>
      <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="4"/>
        <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4l1.4-1.4M17 7l1.4-1.4"/>
      </svg>
    </template>
    <template #default="{ close }">
      <button type="button" class="hmenu__item" @click="setTheme('light'); close()">
        <span class="hmenu__item-row">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="hmenu__leadicon" aria-hidden="true">
            <circle cx="12" cy="12" r="4"/>
            <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4l1.4-1.4M17 7l1.4-1.4"/>
          </svg>
          {{ t('public.menu.theme_light') }}
        </span>
        <svg v-if="themeMode === 'light'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </button>
      <button type="button" class="hmenu__item" @click="setTheme('dark'); close()">
        <span class="hmenu__item-row">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="hmenu__leadicon" aria-hidden="true">
            <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"/>
          </svg>
          {{ t('public.menu.theme_dark') }}
        </span>
        <svg v-if="themeMode === 'dark'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </button>
    </template>
  </HeaderMenu>
</template>

<style scoped>
/* These items are authored here (carry this component's scope), so style them
   directly — no :deep needed even though HeaderMenu renders them in its panel. */
.hmenu__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0.55rem 0.75rem;
  background: transparent;
  border: 0;
  border-radius: 0.4rem;
  color: var(--ink);
  font: inherit;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s;
}
.hmenu__item:hover { background: color-mix(in oklch, var(--ink) 6%, transparent); }
.hmenu__item-row { display: inline-flex; align-items: center; gap: 0.625rem; }
.hmenu__leadicon { width: 1rem; height: 1rem; color: var(--inkSoft); }
.hmenu__check { width: 1rem; height: 1rem; color: var(--accent); flex-shrink: 0; }
</style>
